import { useCallback } from 'react';
import { useAppDispatch } from '../flux/stores/hooks';
import { fetchFolderContents } from '../flux/actions/apsThunks';
import { folderExpandComplete } from '../flux/stores/projectsReducer';
import { useApiAccessToken } from './useApiAccessToken';

// A hard stop so a pathological folder can't page forever. Also, once no
// `page_count` hint has ever arrived, how many *consecutive* empty pages
// to accept as "actually done" — see the walk's own comment below for why
// this can't just be "stop on the first empty page".
const HARD_PAGE_CAP = 300;
const EMPTY_STREAK_LIMIT = 2;
// Autodesk's own `page_count` is documented as "approximate", but fetching
// `folder,rvt` together (rather than `folder` alone) already keeps almost
// everything in a typical folder, so an empty *filtered* page is far less
// likely than it would be filtering to folders only — trust the hint at
// face value rather than padding it, since the padding itself was the
// wasted-round-trip cost this was tuned against.
const PAGE_COUNT_SLACK = 0;

/**
 * Discover one folder's direct children — its subfolders *and* its RVT
 * files — walking `GET /aps/folders/{urn}/contents?type=folder,rvt` page by
 * page and dispatching each page as it resolves, so the tree renders
 * progressively rather than blocking on the whole walk. The folder browser
 * is the source of truth for what's in a folder; `/aps/rvt-models` is never
 * consulted for this and exists only to supply the link graph elsewhere.
 *
 * Why this doesn't stop at the first empty page: the backend filters each
 * page *after* fetching it from Autodesk, so a page can come back with zero
 * matching entries while a later page still holds matches — the service's
 * own docs are explicit about this. So "empty" is evidence, not proof, and
 * the walk instead stops once it has reached Autodesk's own (approximate)
 * page count, or — if that hint never arrives — after a couple of pages in
 * a row have come back empty. A folder that genuinely has neither signal
 * converge is bounded by `HARD_PAGE_CAP` so the walk always finishes.
 */
export function useFolderExpand(projectId) {
    const dispatch = useAppDispatch();
    const { getAccessToken } = useApiAccessToken();

    return useCallback(async (folderUrn) => {
        let accessToken;
        try {
            accessToken = await getAccessToken();
        } catch (error) {
            console.error('Failed to acquire API token to browse ACC folders:', error);
            return;
        }
        if (!accessToken) return;

        let page = 0;
        let pageCountHint = null;
        let consecutiveEmpty = 0;
        let sawRejection = false;

        while (page < HARD_PAGE_CAP) {
            const result = await dispatch(
                fetchFolderContents({ accessToken, projectId, folderUrn, page, types: ['folder', 'rvt'] })
            );
            if (!fetchFolderContents.fulfilled.match(result)) {
                sawRejection = true;
                break;
            }

            const { entries, pageCount } = result.payload;
            if (pageCount !== null) pageCountHint = pageCount;
            consecutiveEmpty = entries.length === 0 ? consecutiveEmpty + 1 : 0;
            page += 1;

            const pastHintedEnd = pageCountHint !== null && page >= pageCountHint + PAGE_COUNT_SLACK;
            const emptyStreakExhausted = pageCountHint === null && consecutiveEmpty >= EMPTY_STREAK_LIMIT;
            if (pastHintedEnd || emptyStreakExhausted) break;
        }

        // A rejected page already left the folder in `error` status via the
        // reducer; marking it `loaded` on top of that would hide the failure.
        if (!sawRejection) dispatch(folderExpandComplete({ projectId, folderUrn }));
    }, [dispatch, getAccessToken, projectId]);
}
